import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Fab,
  Badge,
} from '@mui/material';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useSnackbar } from 'notistack';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { TranscriptionContext } from '../../context/TranscriptionContext';
import { cancelJob } from '../../helper/jobsApi';
import JobQueueItem from './JobQueueItem';

const FINAL_STATUSES = ['completed', 'failed', 'canceled'];
const HIDDEN_STORAGE_KEY = 'transcribe.jobQueueHidden';

function readHiddenPref() {
  try {
    return window.localStorage.getItem(HIDDEN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHiddenPref(hidden) {
  try {
    window.localStorage.setItem(HIDDEN_STORAGE_KEY, hidden ? '1' : '0');
  } catch {
    // ignore
  }
}

export default function JobQueuePanel() {
  const { jobs, dismissRecentJob, clearFinishedJobs } = useContext(TranscriptionContext);
  const { instance } = useMsal();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => readHiddenPref());

  useEffect(() => {
    writeHiddenPref(hidden);
  }, [hidden]);

  const { active, recent } = useMemo(() => {
    const values = Object.values(jobs || {});
    values.sort((a, b) => {
      const aTime = a.updatedAt || a.createdAt || '';
      const bTime = b.updatedAt || b.createdAt || '';
      return aTime < bTime ? 1 : -1;
    });
    const a = [];
    const r = [];
    for (const j of values) {
      if (FINAL_STATUSES.includes(j.status)) r.push(j);
      else a.push(j);
    }
    return { active: a, recent: r };
  }, [jobs]);

  if (active.length === 0 && recent.length === 0) return null;

  const totalRunning = active.length;

  if (hidden) {
    return (
      <Tooltip title="Transkriptions-Queue einblenden" placement="left">
        <Fab
          color="primary"
          onClick={() => setHidden(false)}
          size="medium"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            boxShadow: 4,
          }}
        >
          <Badge
            badgeContent={totalRunning}
            color="error"
            invisible={totalRunning === 0}
            overlap="circular"
          >
            <GraphicEqOutlinedIcon />
          </Badge>
        </Fab>
      </Tooltip>
    );
  }

  const handleCancel = async (job) => {
    try {
      await cancelJob(instance, job.jobId);
    } catch (err) {
      enqueueSnackbar('Job konnte nicht abgebrochen werden.', {
        variant: 'error',
      });
    }
  };

  const handleOpen = (job) => {
    navigate(`/transcription-result/${job.jobId}`);
  };

  const handleDismiss = (job) => {
    dismissRecentJob(job.jobId);
  };

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 360,
        maxHeight: '70vh',
        p: 2,
        borderRadius: 3,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <GraphicEqOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: 0.8, textTransform: 'uppercase' }}
          >
            Transkriptions-Queue
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {totalRunning > 0
              ? `${totalRunning} aktiv${totalRunning === 1 ? '' : 'e'} Job${totalRunning === 1 ? '' : 's'}`
              : 'Keine aktiven Jobs'}
          </Typography>
        </Box>
        <Tooltip title="Ausblenden">
          <IconButton
            size="small"
            onClick={() => setHidden(true)}
            aria-label="Transkriptions-Queue ausblenden"
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ mt: 1.5, overflowY: 'auto', pr: 0.5 }}>
        {active.map((job, idx) => (
          <React.Fragment key={job.jobId}>
            {idx > 0 && <Divider />}
            <JobQueueItem job={job} onCancel={handleCancel} />
          </React.Fragment>
        ))}

        {recent.length > 0 && (
          <>
            {active.length > 0 && <Divider sx={{ my: 1 }} />}
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  flex: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                Kürzlich fertig
              </Typography>
              <Button size="small" onClick={clearFinishedJobs}>
                Löschen
              </Button>
            </Stack>
            {recent.map((job, idx) => (
              <React.Fragment key={job.jobId}>
                {idx > 0 && <Divider />}
                <JobQueueItem
                  job={job}
                  onOpen={handleOpen}
                  onDismiss={handleDismiss}
                />
              </React.Fragment>
            ))}
          </>
        )}
      </Box>
    </Paper>
  );
}
